import { useNavigate, useParams } from "react-router-dom";
import { IconButton } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import { styled } from "@mui/material/styles";

import { PaneListButton, PaneListItemHoverActions } from "src/components/PaneListButton";
import type { LEZ, Scenario } from "src/types";
import { useLEZDeleteMutation } from "src/hooks";
import { IsEnabled } from "./permissions";

interface Props {
  scenario: Scenario;
  lez: LEZ;
  lezs: LEZ[];
}

export function LEZListItem(props: Props) {
  const { scenario, lez, lezs } = props;

  const {
    scenarioId = "",
    lezId,
    caseId = "",
  } = useParams<{
    caseId: string;
    scenarioId: string;
    lezId?: string;
  }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const deleteMutation = useLEZDeleteMutation(scenarioId, lez.id);

  /**
   * Functions
   */
  function handleDelete(lez: LEZ) {
    confirm({
      title: t("label.areYouSure"),
      description: t("confirm.deleteLEZ"),
      confirmationButtonProps: { autoFocus: true },
    }).then(() => {
      deleteMutation.mutate();

      if (lezId === lez.id.toString()) {
        const nextLez = lezs.find((l) => l.id !== lez.id);
        if (nextLez) {
          navigate(`/cases/${caseId}/scenarios/${scenarioId}/lezs/${nextLez.id}`);
        } else {
          navigate(`/cases/${caseId}/scenarios/${scenarioId}/lezs`);
        }
      }
    });
  }

  return render();

  function render() {
    if (!scenario) return null;

    return (
      <Styles>
        <PaneListButton to={`/cases/${caseId}/scenarios/${scenarioId}/lezs/${lez.id}`} className="list-item">
          <div className="name">{lez.name}</div>

          <PaneListItemHoverActions>
            <IsEnabled permission={scenario.permissions.updateLEZ}>
              <IconButton
                aria-label="delete LEZ"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  handleDelete(lez);
                }}
                size="large"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </IsEnabled>
          </PaneListItemHoverActions>
        </PaneListButton>
      </Styles>
    );
  }
}

const Styles = styled("div")`
  .list-item {
    justify-content: space-between;
  }
`;
